/**
 * Marking a screenshot inside the submission panel (#1170): drag to draw a
 * rectangle or an arrow in the theme accent, undo the last mark, apply. The
 * marks are kept separately from the original, so opening the view again
 * continues where the person left off; "Übernehmen" renders them into the
 * PNG that is uploaded.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight, Loader2, Square, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { logError } from '@/lib/logger'
import {
  drawShapes,
  isMeaningfulShape,
  loadImage,
  renderMarkedPng,
  resolveAccentColor,
  strokeWidthFor,
  toImagePoint,
  type MarkupShape,
  type MarkupTool,
} from './markup'
import { FOCUS_RING } from '../notifications/notificationStyles'

interface ScreenshotMarkupProps {
  /** The screenshot as captured, without marks. */
  original: Blob
  shapes: MarkupShape[]
  onApply: (shapes: MarkupShape[], marked: Blob) => void
  onCancel: () => void
}

const TOOLS: Array<{ tool: MarkupTool; Icon: typeof Square }> = [
  { tool: 'rect', Icon: Square },
  { tool: 'arrow', Icon: ArrowUpRight },
]

export function ScreenshotMarkup({ original, shapes: initialShapes, onApply, onCancel }: ScreenshotMarkupProps) {
  const { t } = useTranslation()
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const [failed, setFailed] = useState(false)
  const [tool, setTool] = useState<MarkupTool>('rect')
  const [shapes, setShapes] = useState<MarkupShape[]>(initialShapes)
  const [draft, setDraft] = useState<MarkupShape | null>(null)
  const [applying, setApplying] = useState(false)
  const [color] = useState(resolveAccentColor)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let alive = true
    loadImage(original)
      .then((loaded) => alive && setImage(loaded))
      .catch((error) => {
        logError(error as Error, { context: 'ScreenshotMarkup.load' })
        if (alive) setFailed(true)
      })
    return () => {
      alive = false
    }
  }, [original])

  // The canvas shows the image fitted into the frame; marks are drawn scaled.
  useLayoutEffect(() => {
    const canvas = canvasRef.current
    const frame = frameRef.current
    if (!canvas || !frame || !image) return
    const scale = Math.min((frame.clientWidth || image.naturalWidth) / image.naturalWidth, 1)
    canvas.width = Math.round(image.naturalWidth * scale)
    canvas.height = Math.round(image.naturalHeight * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
    drawShapes(ctx, draft ? [...shapes, draft] : shapes, color, strokeWidthFor(image.naturalWidth), scale)
  }, [image, shapes, draft, color])

  const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect()
    return toImagePoint({ x: event.clientX, y: event.clientY }, box, image!)
  }

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!image || applying) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    const point = pointFrom(event)
    setDraft({ tool, from: point, to: point })
  }

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draft || !image) return
    setDraft({ ...draft, to: pointFrom(event) })
  }

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draft || !image) return
    const finished = { ...draft, to: pointFrom(event) }
    setDraft(null)
    if (isMeaningfulShape(finished)) setShapes((current) => [...current, finished])
  }

  const apply = async () => {
    if (!image) return
    setApplying(true)
    try {
      onApply(shapes, shapes.length > 0 ? await renderMarkedPng(image, shapes, color) : original)
    } catch (error) {
      logError(error as Error, { context: 'ScreenshotMarkup.apply', shapes: shapes.length })
      setFailed(true)
    } finally {
      setApplying(false)
    }
  }

  return (
    <section data-testid="ticket-panel-markup" aria-labelledby="ticket-panel-markup-title" className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 id="ticket-panel-markup-title" className="text-sm font-medium text-[var(--text-color)]">
          {t('ticketPanel.markup.title')}
        </h3>
        <div className="flex items-center gap-1">
          <div role="group" aria-label={t('ticketPanel.markup.toolsLabel')} className="flex gap-1">
            {TOOLS.map(({ tool: value, Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={tool === value}
                data-testid={`ticket-panel-markup-tool-${value}`}
                onClick={() => setTool(value)}
                title={t(`ticketPanel.markup.tool.${value}`)}
                className={`flex h-8 items-center gap-1.5 rounded-md border px-2 text-xs ${FOCUS_RING} ${
                  tool === value
                    ? 'border-[var(--theme-interactive-color)] bg-[color-mix(in_srgb,var(--theme-interactive-color)_14%,transparent)] text-[var(--text-color)]'
                    : 'border-[var(--window-border-color)] text-[var(--text-secondary)] hover:bg-[var(--menu-item-hover)]'
                }`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {t(`ticketPanel.markup.tool.${value}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShapes((current) => current.slice(0, -1))}
            disabled={shapes.length === 0}
            aria-label={t('ticketPanel.markup.undo')}
            title={t('ticketPanel.markup.undo')}
            data-testid="ticket-panel-markup-undo"
            className={`flex h-8 w-8 items-center justify-center rounded-md text-[var(--text-secondary)] hover:bg-[var(--menu-item-hover)] hover:text-[var(--text-color)] disabled:pointer-events-none disabled:opacity-40 ${FOCUS_RING}`}
          >
            <Undo2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      <p className="text-xs text-[var(--text-secondary)]">{t('ticketPanel.markup.hint')}</p>

      <div
        ref={frameRef}
        className="flex min-h-0 flex-1 items-start justify-center overflow-auto rounded-lg border border-[var(--window-border-color)] bg-[var(--menu-item-hover)]"
      >
        {failed ? (
          <p role="alert" className="p-4 text-sm text-[var(--text-secondary)]">
            {t('ticketPanel.markup.failed')}
          </p>
        ) : image ? (
          <canvas
            ref={canvasRef}
            data-testid="ticket-panel-markup-canvas"
            data-shapes={shapes.length}
            role="img"
            aria-label={t('ticketPanel.markup.canvasLabel', { count: shapes.length })}
            className="block max-w-full touch-none cursor-crosshair"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => setDraft(null)}
          />
        ) : (
          <Loader2 className="m-6 h-5 w-5 animate-spin text-[var(--text-secondary)]" aria-label={t('common.loading')} />
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={applying}>
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void apply()}
          disabled={!image || applying}
          data-testid="ticket-panel-markup-apply"
        >
          {applying && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />}
          {t('ticketPanel.markup.apply')}
        </Button>
      </div>
    </section>
  )
}
